// Legacy token-based portal preserved for already-enrolled candidates from before the auth overhaul.
// New candidates use /enroll/portal with email+password auth via EnrollPortal.jsx.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Award, CheckCircle2, Lock, ShieldCheck, GraduationCap, Lightbulb, Download } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { API } from "../lib/api";

export default function EnrollPortalLegacy() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/enroll/${token}`);
      setData(res.data);
      const firstUnanswered = res.data.days.find((d) => d.unlocked && !d.answered);
      setActiveDay(firstUnanswered?.day || res.data.current_day);
    } catch {
      toast.error("Couldn't load your cohort. Check the link?");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (answer.trim().length < 20) {
      toast.error("Please write at least a couple of sentences (20+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enroll/${token}/answer`, { day: activeDay, answer });
      toast.success(`Day ${activeDay} submitted!`);
      setAnswer("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to submit answer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-ivory flex items-center justify-center text-navy/60">Loading your cohort…</div>;
  if (!data) return <div className="min-h-screen bg-ivory flex items-center justify-center text-navy/60">Cohort not found.</div>;

  const dayObj = data.days.find((d) => d.day === activeDay) || data.days[0];
  const completed = data.days_completed >= data.total_days;
  const progressPct = Math.round((data.days_completed / data.total_days) * 100);

  return (
    <div className="min-h-screen bg-ivory" data-testid="legacy-portal-page">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-[#10B981]/15 flex items-center justify-center ring-1 ring-[#10B981]/30">
              <GraduationCap className="h-4 w-4 text-[#10B981]" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">PRC Cohort Portal</div>
              <div className="eyebrow text-[10px] text-[#D4AF37]">Direct Hire Team</div>
            </div>
          </Link>
          <div className="text-right hidden sm:block">
            <div className="text-xs text-white/55">Welcome,</div>
            <div className="text-sm font-semibold">{data.full_name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="card-lux !p-7" data-testid="portal-progress">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow text-[#10B981]">{data.track} cohort · in progress</div>
              <h1 className="mt-2 font-serif text-3xl md:text-4xl text-navy leading-tight">
                {completed ? "All days complete." : `Day ${data.current_day} of ${data.total_days}`}
              </h1>
              <p className="mt-2 text-sm text-navy/60">{completed ? "Our placement team is reviewing your responses." : `${data.days_completed} of ${data.total_days} days submitted · keep going.`}</p>
            </div>
            <div className="font-mono text-xs tracking-[0.3em] text-navy/45">{progressPct}% COMPLETE</div>
          </div>
          <div className="mt-6 h-2 rounded-full bg-navy/5 overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {data.days.map((d) => {
              const isActive = d.day === activeDay;
              let cls;
              if (d.answered) cls = "bg-[#10B981] text-white border-[#10B981]";
              else if (d.unlocked) cls = `bg-white text-navy border-navy/15 hover:border-[#10B981] ${isActive ? "ring-2 ring-[#10B981]" : ""}`;
              else cls = "bg-navy/5 text-navy/35 border-navy/10 cursor-not-allowed";
              return (
                <button key={d.day} type="button" disabled={!d.unlocked} onClick={() => { setActiveDay(d.day); setAnswer(""); }} className={`px-3.5 py-2 rounded-md text-xs font-semibold border transition-all ${cls}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {d.answered ? <CheckCircle2 className="h-3.5 w-3.5" /> : !d.unlocked ? <Lock className="h-3 w-3" /> : null}
                    Day {d.day}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {data.prc_id && (
          <div className="mt-6 card-lux !p-7 bg-gradient-to-br from-[#D4AF37]/10 to-[#10B981]/5 border-[#D4AF37]/30">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="h-12 w-12 rounded-md bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <Award className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-[280px]">
                <div className="eyebrow text-[#D4AF37]">PRC Earned</div>
                <h2 className="mt-1 font-serif text-2xl text-navy">Verification ID: <span className="font-mono">{data.prc_id}</span></h2>
              </div>
              <div className="flex flex-col gap-2.5 w-full sm:w-auto">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/enroll/${token}/certificate.pdf`} target="_blank" rel="noopener noreferrer" className="btn-emerald !py-2.5 !px-5 text-sm">
                  <Download className="h-4 w-4" /> Download Certificate
                </a>
                <a href={`/verify/${data.prc_id}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5 !px-5 text-sm !justify-center">Public verify page</a>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <div className="card-lux !p-8">
              {!dayObj.unlocked ? (
                <div className="text-center py-12">
                  <Lock className="h-10 w-10 text-navy/30 mx-auto" />
                  <h2 className="mt-4 font-serif text-2xl text-navy">Day {dayObj.day} unlocks soon</h2>
                  <p className="mt-2 text-sm text-navy/60">Each question unlocks 24 hours after the previous one.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#10B981]">DAY {dayObj.day} OF {data.total_days}</span>
                    {dayObj.answered && <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/10 text-[#10B981] px-2.5 py-0.5 text-xs font-semibold"><CheckCircle2 className="h-3 w-3" /> Submitted</span>}
                  </div>
                  <h2 className="mt-4 font-serif text-3xl md:text-4xl text-navy leading-tight">{dayObj.title}</h2>
                  <p className="mt-5 text-base text-navy/80 leading-relaxed">{dayObj.question}</p>
                  {dayObj.hint && (
                    <div className="mt-6 rounded-md border border-[#D4AF37]/30 bg-[#FFFCF0] p-4 flex items-start gap-3 text-sm text-navy/75">
                      <Lightbulb className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <div><strong className="text-navy">Hint:</strong> {dayObj.hint}</div>
                    </div>
                  )}
                  {dayObj.answered ? (
                    <div className="mt-7">
                      <div className="eyebrow text-navy/45">Your answer</div>
                      <div className="mt-2 rounded-md border border-navy/10 bg-navy/[0.02] p-5 text-sm text-navy/80 leading-relaxed whitespace-pre-wrap">{dayObj.answer_text}</div>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="mt-7">
                      <Textarea rows={8} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Take your time." />
                      <div className="mt-2 flex items-center justify-between text-xs text-navy/50">
                        <span>{answer.trim().length} characters · 20+ minimum</span>
                      </div>
                      <button type="submit" disabled={submitting || answer.trim().length < 20} className="btn-emerald mt-5 w-full disabled:opacity-50">
                        {submitting ? "Submitting…" : `Submit Day ${dayObj.day}`}
                        {!submitting && <ArrowRight className="h-4 w-4" />}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
          <aside className="md:col-span-4">
            <div className="card-lux !p-6 sticky top-6">
              <div className="eyebrow text-[#D4AF37]">Cohort details</div>
              <div className="mt-3 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-navy/55">Name</span><span className="text-navy font-medium">{data.full_name}</span></div>
                <div className="flex justify-between"><span className="text-navy/55">Track</span><span className="text-navy font-medium">{data.track}</span></div>
                <div className="flex justify-between"><span className="text-navy/55">Days</span><span className="text-navy font-medium">{data.days_completed} / {data.total_days}</span></div>
              </div>
              <div className="mt-6 rounded-md bg-[#F6FDF9] border border-[#10B981]/20 p-4 flex items-start gap-2.5 text-xs text-navy/70 leading-relaxed">
                <ShieldCheck className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                <div>This is your private cohort link. We've also emailed it to <strong>{data.email}</strong>.</div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
